/**
 * Iter-156C Phase 3 — Read-only banner shown to Contributors and
 * Viewers when the project's script is restricted to Administrators.
 *
 * Calm, non-destructive copy per the user steer:
 *
 *   "This project's script is restricted to Administrators. You can
 *    still view scenes, characters and continuity records. If you
 *    need script access, please speak to the project owner or an
 *    administrator."
 *
 * No "Request access" CTA. No nagware. The signal is informational —
 * Slack / email / a tap on the shoulder is the right channel for an
 * access ask.
 *
 * Visual: a tinted card with the Lucide `Lock` icon, sitting where
 * the PDF preview would normally render. Replaces the inline iframe
 * (and the Download / Open buttons) entirely — see ScriptPage.jsx.
 */
import { Lock } from "lucide-react";

export default function ScriptLockedBanner({ filename }) {
    return (
        <section
            data-testid="script-locked-banner"
            className="rounded-xl border border-white/10 bg-card p-6 mt-4"
        >
            <div className="flex items-start gap-3">
                <div
                    aria-hidden
                    className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-200"
                >
                    <Lock size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-sky-200 font-semibold">
                        Restricted
                    </div>
                    <h2
                        data-testid="script-locked-banner-title"
                        className="text-base text-foreground mt-1 font-semibold leading-snug"
                    >
                        Script restricted to Administrators
                    </h2>
                    <p
                        data-testid="script-locked-banner-body"
                        className="text-sm text-neutral-300 mt-2 leading-relaxed"
                    >
                        This project&rsquo;s script is restricted to
                        Administrators. You can still view scenes,
                        characters and continuity records.
                    </p>
                    <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
                        If you need script access, please speak to the
                        project owner or an administrator.
                    </p>
                    {filename ? (
                        <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
                            Script on file:{" "}
                            <span className="text-neutral-400">{filename}</span>
                        </p>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
